// THE FAKE'S THREE PROPERTIES — the prototype's three fixtures, re-expressed as contract shapes.
//
// They are the same three addresses so that anyone who walked the prototype recognises the flow:
// a Pennsylvania property that builds a letter, an Ohio address in a state PROppeal does not serve,
// and a Nebraska property whose sales do not support a reduction.
//
// WHAT CHANGED FROM THE PROTOTYPE'S FIXTURES, and why each one had to:
//   • Implied market is COMPUTED, not stored. The prototype stored 398,500 beside an assessed value
//     and a ratio that multiply out to 398,522 (WEB_PATHWAY_INVENTORY.md §2 row A10). Two writers for
//     one number is how they drift.
//   • The letter's arithmetic adds up. The prototype printed "$360,000 … $19,500 of condition work
//     brings a buyer to $341,000" (§4 "Worse" #2); 360,000 − 19,500 is 340,500, and that is the ask.
//   • Nebraska's gap is not a negative number rendered as a saving. `$-5,500` (§4 "Worse" #3) was a
//     client subtraction; here the sales land ABOVE the assessment and the server says there is no
//     case, which is what that number was trying to say.
//   • Every number carries its basis. `Money<"assessed">` and `Money<"market">` are different types,
//     so the mix-up behind the prototype's ratio arithmetic cannot be written here.
//   • Nothing is "supported: true". Eligibility is G-1's answer, not a field on a property.
//
// These are FIXTURES FOR A FAKE. They are not golden values and nothing is pinned to them.

import type {
  AttestationCopy, CanRaise, CapRegime, CompView, Copy, Jurisdiction, LegalDocRef,
  Money, PinnedCopy, Ratio, SubjectView,
} from "@contract";
import { v2Copy, v2DatedCopy, v2StateCopy } from "@v2-copy";

export type ScenarioId = "pa" | "oh" | "ne";

const market = (amount: number): Money<"market"> => ({ basis: "market", amount });
const assessed = (amount: number): Money<"assessed"> => ({ basis: "assessed", amount });

/** Provenance, as `SubjectView` carries it: every field says where it came from and when. */
const fromProvider = <T>(value: T, retrievedAt: string) =>
  ({ value, source: "provider" as const, provider: "realie" as const, retrievedAt });

export interface Scenario {
  readonly id: ScenarioId;
  readonly typedAddress: string;
  readonly resolvedAddress: string;
  readonly label: string;
  readonly sublabel: string;
  readonly tag: string;
  readonly state: string;
  readonly stateName: string;
  readonly county: string;
  /** Whether G-1 passes for this state. `null` means the address has no state at all. */
  readonly servedState: boolean;
  readonly jurisdiction: Jurisdiction;
  readonly ratio: Ratio;
  readonly canRaise: CanRaise;
  readonly capRegime: CapRegime;
  readonly subject: SubjectView | null;
  readonly impliedMarket: Money<"market"> | null;
  readonly comps: readonly CompView[];
  readonly compAdjustments: Readonly<Record<string, { adjusted: Money<"market"> | null; text: string }>>;
  /** The engine's answer, or null when the sales do not support a reduction. */
  readonly indicatedValue: Money<"market"> | null;
  readonly suggestedValue: Money<"market"> | null;
  readonly suggestionRange: { low: Money<"market">; high: Money<"market"> } | null;
  readonly supportsReduction: boolean;
  readonly confidence: "high" | "medium" | "low";
  readonly confidenceBlurb: Copy;
  readonly conditionDeduction: Money<"market"> | null;
  /** WEB-PARITY-4 (2.21.0): each item with the core's own tags, as the server now carries them. */
  readonly conditionItems: readonly {
    label: string; amount: Money<"market">; method: string; category: string; confidence: string; basis: string;
  }[];
  readonly requestedValue: Money<"market"> | null;
  readonly estimatedReduction: { amount: Money<"assessed">; percent: number } | null;
  readonly caseTier: string;
  readonly caseTierLadder: readonly string[];
  readonly narrative: { headline: string; explanationPoints: string[]; source: "v1.evaluateCase" } | null;
  /** WEB-PARITY-4 (2.21.0, VD-3): the check's verdict, caveats and picks, as the server's narrative now carries them. */
  readonly verdict: "strongCase" | "possibleCase" | "borderline" | "inconclusive" | "insufficientData" | null;
  readonly caveats: string;
  readonly strongestCompIds: readonly string[];
  readonly noCase: boolean;
  readonly letterText: string | null;
  readonly exhibitConclusion: string | null;
}

const LADDER = ["Solid", "Very strong", "Strong", "Fair", "Thin", "Other grounds"] as const;

// ── Pennsylvania: the case that builds a letter ──────────────────────────────────────────────────

const PA_RATIO = 0.2976;
const PA_ASSESSED = 118600;
const PA_IMPLIED = Math.round(PA_ASSESSED / PA_RATIO); // 398,522 — computed, never stored twice.
const PA_INDICATED = 360000;
const PA_CONDITION = 19500;
const PA_REQUESTED = PA_INDICATED - PA_CONDITION; // 340,500. The prototype printed 341,000.
const PA_REDUCTION = Math.round(PA_ASSESSED - PA_REQUESTED * PA_RATIO); // 17,267

const paComps: CompView[] = [
  cmp("pa-1", "1418 Foxhill Rd", 352000, "2025-08-14", 2010, 4, 2.5, 0.4, true),
  cmp("pa-2", "906 Windermere Ave", 368000, "2025-10-02", 2180, 4, 3, 0.6, true),
  cmp("pa-3", "233 Cricket Lane", 355000, "2025-11-20", 2060, 4, 2.5, 0.7, true),
  cmp("pa-4", "74 Brookmead Dr", 349500, "2025-09-08", 1985, 3, 2.5, 0.5, true),
  cmp("pa-5", "1802 Heatherwood Ct", 364000, "2025-12-05", 2125, 4, 2.5, 0.8, true),
  cmp("pa-6", "9 Quarry Ridge Rd", 431000, "2024-02-02", 2740, 5, 3.5, 1.9, false),
];

function cmp(
  compId: string, address: string, price: number, saleDate: string, sqft: number,
  beds: number, baths: number, distanceMi: number, included: boolean,
): CompView {
  return {
    compId, address, salePrice: market(price), saleDate, sqft, beds, baths, distanceMi,
    // `source` is `provider` or `owner` since contract 2.5.0 (W2.5a): v1's merged pool carries no per-comp
    // attribution, so a vendor name here would be a fabricated citation. The fake says what the server says.
    propertyType: "Single-family", distressed: null, source: "provider",
    included, usable: true, keptDespiteOutlier: false,
  };
}

export const PA: Scenario = {
  id: "pa",
  typedAddress: "412 Sycamore Lane, Lansdale, PA 19446",
  resolvedAddress: "412 Sycamore Ln, Lansdale, PA 19446",
  label: "412 Sycamore Lane",
  sublabel: "Lansdale, PA 19446 · Montgomery County",
  tag: "Builds a letter",
  state: "PA",
  stateName: "Pennsylvania",
  county: "Montgomery",
  servedState: true,
  jurisdiction: {
    state: "PA",
    county: "Montgomery",
    fips: "42091",
    addressee: "Montgomery County Board of Assessment Appeals",
    filingTerm: "appeal",
    deadline: {
      kind: "countyVariant",
      resolved: {
        at: "county",
        date: {
          authority: "county",
          date: "2027-08-01",
          // The rulebook's provenance for Montgomery (functions/src/deadlines/table.ts), as the server sends it.
          verification: { source: "montgomerycountypa.gov/464 deadline notice", verifiedOn: "2026-09-10" },
        },
      },
      label: { code: "fake.deadline.label", text: "Filing deadline" },
      // A county's own date carries the county sentence, as the server's projection sends it (filingDeadline.ts); it was
      // the fixed-statute sentence here until the merge of `main` (R233), which no county row sends.
      notice: v2DatedCopy("resolve.t2.notice.countyVariant.county", "August 1, 2027"),
    },
    protestWindow: {
      standing: "inside",
      closes: {
        authority: "county",
        date: "2027-08-01",
        verification: { source: "Montgomery County Board of Assessment Appeals", verifiedOn: "2026-09-15" },
      },
      filedWith: "Montgomery County Board of Assessment Appeals",
      notice: { code: "fake.protest.inside", text: "You are inside the window to file for this tax year." },
    },
  },
  ratio: {
    value: PA_RATIO,
    isFractional: true,
    origin: "published",
    // `RatioConfidence`'s raw value as the server's V-5 carries it for a PA county CLR (`resolve/ratio.ts`, case "PA") —
    // the one confidence the app shows its ratio card for (WEB-PARITY-1C; SubjectPropertyView.swift:2005).
    confidence: "needsLocalRatio",
    basis: {
      code: "fake.ratio.basis.pa",
      text:
        "Pennsylvania assesses on a frozen base year, so the county's number is converted through " +
        "Montgomery's STEB Common Level Ratio to reach a market value.",
    },
    effectiveFrom: "2026-07-01",
    effectiveTo: "2027-06-30",
  },
  // WEB-PARITY-2B: the card the SERVER sends for Pennsylvania — the app's own, ported (resolve/canRaiseDecision.ts
  // `stateCard`). It was a sentence written for the fake.
  canRaise: {
    risk: "canRaise",
    requiresAcknowledgment: true,
    card: v2Copy("resolve.g2.card.pa"),
    noCaseSentence: null,
  },
  capRegime: { kind: "none", isAcquisitionValue: false, recognized: true, copy: null },
  subject: {
    address: "412 Sycamore Ln, Lansdale, PA 19446",
    unit: null,
    state: "PA",
    county: "Montgomery",
    parcel: fromProvider("33-00-04821-007", "2026-09-17"),
    propertyType: fromProvider("Single-family", "2026-09-17"),
    assessedValue: fromProvider(assessed(PA_ASSESSED), "2026-09-17"),
    marketValue: null,
    impliedMarketValue: market(PA_IMPLIED),
    sqft: fromProvider(2100, "2026-09-17"),
    beds: fromProvider(4, "2026-09-17"),
    baths: fromProvider(2.5, "2026-09-17"),
    yearBuilt: fromProvider(1994, "2026-09-17"),
    lastSale: null,
    // WEB-PARITY-1A (2.14.0): the lot, the assessed value's year and the place, as the record carries them.
    lotSizeSqft: fromProvider(10890, "2026-09-17"),
    assessedYear: fromProvider(2026, "2026-09-17"),
    coordinates: { latitude: 40.2415, longitude: -75.2835 },
  },
  impliedMarket: market(PA_IMPLIED),
  comps: paComps,
  compAdjustments: {
    "pa-1": { adjusted: market(360000), text: "+90 sq ft at the derived rate; sold 13 months before the valuation date" },
    "pa-2": { adjusted: market(361000), text: "−80 sq ft at the derived rate; sold 11 months before the valuation date" },
    "pa-3": { adjusted: market(358500), text: "+40 sq ft at the derived rate; sold 10 months before the valuation date" },
    "pa-4": { adjusted: market(359500), text: "+115 sq ft at the derived rate; sold 12 months before the valuation date" },
    "pa-5": { adjusted: market(362000), text: "−25 sq ft at the derived rate; sold 9 months before the valuation date" },
    "pa-6": { adjusted: null, text: "Outside the pool: 1.9 miles away, 640 sq ft larger, and sold 20 months before the valuation date" },
  },
  indicatedValue: market(PA_INDICATED),
  suggestedValue: market(PA_REQUESTED),
  suggestionRange: { low: market(336000), high: market(345000) },
  supportsReduction: true,
  confidence: "high",
  confidenceBlurb: {
    code: "fake.value.confidence.pa",
    text: "Five sales within 0.8 miles, landing within $3,500 of one another after adjustment.",
  },
  conditionDeduction: market(PA_CONDITION),
  conditionItems: [
    {
      label: "Roof, original 1994, shingle tabs cracked through", amount: market(12400), method: "cost-to-cure",
      category: "roof", confidence: "high",
      basis: "Typical replacement cost for an architectural-shingle roof of this size, from a national reference table.",
    },
    {
      label: "Foundation crack with efflorescence, north wall", amount: market(7100), method: "cost-to-cure",
      category: "structural", confidence: "low",
      basis: "Typical repair cost for a sealed and pinned wall crack; a structural engineer's report would firm it up.",
    },
  ],
  requestedValue: market(PA_REQUESTED),
  estimatedReduction: { amount: assessed(PA_REDUCTION), percent: 14.56 },
  caseTier: "Very strong",
  caseTierLadder: [...LADDER],
  narrative: {
    headline: "The sales support a lower value",
    explanationPoints: [
      "Five recorded sales within 0.8 miles, all adjusted to your home's finished size.",
      "After adjustment they land within $3,500 of one another, which is a tight cluster for this market.",
      "The condition items you described are costed from a national reference table at the typical figure.",
    ],
    source: "v1.evaluateCase",
  },
  verdict: "strongCase",
  caveats: "This is a data check against recorded sales, not an appraisal.",
  strongestCompIds: ["pa-1", "pa-3", "pa-5"],
  noCase: false,
  letterText:
    "To the Montgomery County Board of Assessment Appeals\n\n" +
    "I am the owner of 412 Sycamore Ln, Lansdale, PA 19446, parcel 33-00-04821-007, and I am appealing " +
    "its assessed value for the coming tax year.\n\n" +
    "The property is assessed at $118,600. Applied through Montgomery County's Common Level Ratio of " +
    "0.2976, that assessment implies a market value of $398,522.\n\n" +
    "Five recorded sales within 0.8 miles of the property, adjusted to its finished area, indicate a " +
    "market value of $360,000. The property's roof and north foundation wall require work costed at " +
    "$19,500 from a national reference table at the typical figure, which brings an informed buyer to " +
    "$340,500.\n\n" +
    "I respectfully request that the assessed value be reduced to $101,333, which is $340,500 carried " +
    "through the Common Level Ratio.\n\n" +
    "The comparable sales and their adjustments are set out in the attached exhibit.",
  exhibitConclusion: "Taken together, the sales in the exhibit indicate a market value of about $360,000.",
};

// ── Ohio: a state PROppeal does not serve ────────────────────────────────────────────────────────

export const OH: Scenario = {
  id: "oh",
  typedAddress: "1847 Fairmount Ave, Columbus, OH 43201",
  resolvedAddress: "1847 Fairmount Ave, Columbus, OH 43201",
  label: "1847 Fairmount Ave",
  sublabel: "Columbus, OH 43201 · Franklin County",
  tag: "State not open",
  state: "OH",
  stateName: "Ohio",
  county: "Franklin",
  servedState: false,
  jurisdiction: {
    state: "OH", county: "Franklin", fips: "39049", addressee: null,
    filingTerm: "appeal", deadline: null, protestWindow: { standing: "unknown" },
  },
  ratio: {
    value: 0.35, isFractional: true, origin: "assumedFullValue", confidence: "low",
    basis: { code: "fake.ratio.basis.oh", text: "Ohio's ratio has not been verified for this county." },
    effectiveFrom: null, effectiveTo: null,
  },
  // WEB-COPY-1: the card the SERVER sends for a state whose own card is not ported — every state, today — is the
  // no-card sentence naming it (functions/src/v2/resolve/canRaiseDecision.ts `noCardSentence`).
  // Ohio's TIER is the server's `canRaise` (valuation/canRaise.ts `CAN_RAISE_STATES`, pinned to the app's); the fake said
  // `unknown` until WEB-PARITY-2B, which the website's can-raise card now reads.
  canRaise: {
    risk: "canRaise",
    requiresAcknowledgment: true,
    card: v2StateCopy("resolve.g2.noCard", "Ohio"),
    noCaseSentence: null,
  },
  capRegime: { kind: "none", isAcquisitionValue: false, recognized: false, copy: null },
  subject: null,
  impliedMarket: null,
  comps: [],
  compAdjustments: {},
  indicatedValue: null,
  suggestedValue: null,
  suggestionRange: null,
  supportsReduction: false,
  confidence: "low",
  confidenceBlurb: { code: "fake.value.confidence.oh", text: "No valuation was run for this property." },
  conditionDeduction: null,
  conditionItems: [],
  requestedValue: null,
  estimatedReduction: null,
  caseTier: "Thin",
  caseTierLadder: [...LADDER],
  narrative: null,
  verdict: null,
  caveats: "",
  strongestCompIds: [],
  noCase: false,
  letterText: null,
  exhibitConclusion: null,
};

// ── Nebraska: the sales do not support a reduction ───────────────────────────────────────────────

const NE_ASSESSED = 282500;

const neComps: CompView[] = [
  cmp("ne-1", "1120 Cedar St", 291500, "2026-06-11", 1780, 3, 2, 0.4, true),
  cmp("ne-2", "4407 Poppleton Ave", 279000, "2026-04-02", 1690, 3, 2, 0.3, true),
  cmp("ne-3", "512 S 49th St", 296000, "2026-05-19", 1810, 3, 2.5, 0.6, true),
];

export const NE: Scenario = {
  id: "ne",
  typedAddress: "88 Birch Ct, Omaha, NE 68106",
  resolvedAddress: "88 Birch Ct, Omaha, NE 68106",
  label: "88 Birch Ct",
  sublabel: "Omaha, NE 68106 · Douglas County",
  tag: "No case to file",
  state: "NE",
  stateName: "Nebraska",
  county: "Douglas",
  servedState: true,
  jurisdiction: {
    state: "NE",
    county: "Douglas",
    fips: "31055",
    addressee: "Douglas County Board of Equalization",
    filingTerm: "protest",
    deadline: {
      kind: "fixedStatutory",
      date: { authority: "statutory", date: "2027-06-30", citation: "Neb. Rev. Stat. §77-1502" },
      label: { code: "fake.deadline.label", text: "Filing deadline" },
      notice: v2DatedCopy("resolve.t2.notice.fixedStatutory", "June 30, 2027"),
    },
    protestWindow: {
      standing: "outside",
      nextOpens: { authority: "statutory", date: "2027-06-01", citation: "Neb. Rev. Stat. §77-1502" },
      filedWith: "Douglas County Clerk",
      notice: {
        code: "fake.protest.outside",
        text: "Today is outside Nebraska's protest window. The next one opens June 1, 2027 and closes June 30, 2027.",
      },
      earlier: null,
    },
  },
  ratio: {
    value: 1.0,
    isFractional: false,
    origin: "genuineFullValue",
    // `RatioConfidence`'s raw value as the server's V-5 carries it for Nebraska (`resolve/ratio.ts`, case "NE"), which is
    // why the app shows no ratio card there (WEB-PARITY-1C; SubjectPropertyView.swift:2005).
    confidence: "statutory",
    basis: {
      code: "fake.ratio.basis.ne",
      text:
        "Nebraska assesses at 100% of actual value (Neb. Rev. Stat. §77-201), so the county's number " +
        "meets comparable sales directly — there is no ratio in between.",
    },
    effectiveFrom: "2026-01-01",
    effectiveTo: "2026-12-31",
  },
  // WEB-PARITY-2B: the card the SERVER sends for Nebraska — the app's `.canRaise` card for it, ported.
  canRaise: {
    risk: "canRaise",
    requiresAcknowledgment: true,
    card: v2Copy("resolve.g2.card.ne"),
    noCaseSentence: {
      code: "fake.canraise.nocase.ne",
      text: "Nebraska's board can raise an assessment as well as lower it, which is the risk in filing without support.",
    },
  },
  capRegime: { kind: "none", isAcquisitionValue: false, recognized: true, copy: null },
  subject: {
    address: "88 Birch Ct, Omaha, NE 68106",
    unit: null,
    state: "NE",
    county: "Douglas",
    parcel: fromProvider("1523-0044-19", "2026-09-17"),
    propertyType: fromProvider("Single-family", "2026-09-17"),
    assessedValue: fromProvider(assessed(NE_ASSESSED), "2026-09-17"),
    marketValue: null,
    impliedMarketValue: market(NE_ASSESSED),
    sqft: fromProvider(1740, "2026-09-17"),
    beds: fromProvider(3, "2026-09-17"),
    baths: fromProvider(2, "2026-09-17"),
    yearBuilt: fromProvider(2003, "2026-09-17"),
    lastSale: null,
    lotSizeSqft: fromProvider(7405, "2026-09-17"),
    assessedYear: fromProvider(2026, "2026-09-17"),
    coordinates: { latitude: 41.2404, longitude: -95.9967 },
  },
  impliedMarket: market(NE_ASSESSED),
  comps: neComps,
  compAdjustments: {
    "ne-1": { adjusted: market(288000), text: "−40 sq ft at the derived rate; sold 3 months before the valuation date" },
    "ne-2": { adjusted: market(283500), text: "+50 sq ft at the derived rate; sold 5 months before the valuation date" },
    "ne-3": { adjusted: market(290000), text: "−70 sq ft at the derived rate; sold 4 months before the valuation date" },
  },
  indicatedValue: market(288000),
  suggestedValue: market(288000),
  suggestionRange: { low: market(283500), high: market(290000) },
  supportsReduction: false,
  confidence: "high",
  confidenceBlurb: {
    code: "fake.value.confidence.ne",
    text: "Three sales within 0.6 miles, all landing above the county's own figure after adjustment.",
  },
  conditionDeduction: null,
  conditionItems: [],
  requestedValue: null,
  estimatedReduction: null,
  caseTier: "Other grounds",
  caseTierLadder: [...LADDER],
  narrative: {
    headline: "The county has you about right",
    explanationPoints: [
      "Three recorded sales within 0.6 miles, adjusted to your home's finished size.",
      "All three land above the county's own figure, so the sales do not support a lower value.",
      "Nothing you have told us so far is an independent ground a board could act on.",
    ],
    source: "v1.evaluateCase",
  },
  verdict: "inconclusive",
  caveats: "",
  strongestCompIds: [],
  noCase: true,
  letterText: null,
  exhibitConclusion: null,
};

export const SCENARIOS: Readonly<Record<ScenarioId, Scenario>> = { pa: PA, oh: OH, ne: NE };
export const SCENARIO_ORDER: readonly ScenarioId[] = ["pa", "oh", "ne"];

/** The state-gate refusal the server writes for a state it does not serve yet. */
export const comingSoonCopy = (stateName: string): Copy => v2StateCopy("resolve.g1.comingSoon", stateName);

/** The staging bypass's sentence — `info`, with `supported` still false. */
export const stagingBypassCopy = (): Copy => v2Copy("resolve.g1.stagingBypass");

export type LegalBlock = {
  accepted: unknown;
  terms: LegalDocRef;
  privacy: LegalDocRef;
  attestations: AttestationCopy[];
  changeSummary: PinnedCopy[];
  privacyChangeSummary: PinnedCopy[];
};
